import { useState } from "react";
import {
    Box,
    Button,
    TextField,
    useMediaQuery,
    Typography,
    useTheme,
    Container,
    Grid,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLogin } from "state";
import validator from "validator";
import { Snackbar, Alert } from "@mui/material";
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import logo from './img/logo.png';

// Login validation schema
const loginSchema = yup.object().shape({
    email: yup.string()
        .test("is-valid-email", "Invalid email", (value) => validator.isEmail(value))
        .required("Required"),
    password: yup.string().required("Required"),
});

// Initial values for login form
const initialValuesLogin = {
    email: "",
    password: "",
};

const Login = () => {
    const { palette } = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isNonMobile = useMediaQuery("(min-width:600px)");
    const isSmallScreen = useMediaQuery("(max-width:900px)");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [passwordVisible, setPasswordVisible] = useState(false);

    // Login function to handle form submission
    const login = async (values, onSubmitProps) => {
        const loggedInResponse = await fetch(
            "https://my-pers-thing-server.onrender.com/auth/login",
            {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(values),
            }
        );
        if (loggedInResponse.ok) {
            const loggedIn = await loggedInResponse.json();
            dispatch(
                setLogin({
                    user: loggedIn.user,
                    token: loggedIn.token,
                })
            );
            navigate("/home");
        } else {
            const errorData = await loggedInResponse.json();
            setSnackbarMessage(errorData?.message || "Invalid email or password");
            setSnackbarOpen(true);
        }
    };

    const handleFormSubmit = async(values, onSubmitProps) => {
        await login(values, onSubmitProps);
    };

    return (
        <Container maxWidth="xl" sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            flexDirection: 'column',
            pt: { xs: 2, md: 2 },
            position: 'relative',
            paddingBottom: { xs: '100px', sm: '120px', md: '80px' } // Add padding to make space for the warning
        }}>
            <style>
                {`
                    body {
                        margin: 0;
                        padding: 0;
                        overflow-x: hidden;
                    }
                `}
            </style>
            
            {/* Snackbar for showing error messages */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity="error"
                    sx={{
                        width: "100%",
                        fontSize: "16px",
                        borderRadius: "12px",
                        "& .MuiAlert-icon": {
                            fontSize: "24px",
                        },
                    }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            
            {/* Header text section - moved up with reduced margins */}
            <Box sx={{ 
                textAlign: "center", 
                mt: { xs: 0, md: 1 },
                mb: { xs: 0, md: 1 }
            }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontSize: { xs: "20px", sm: "22px", md: "25px" },
                        color: "#3B3B3B",
                        textTransform: "uppercase",
                        mb: 0.5
                    }}
                >
                    A Social Platform to Showcase & Discover Talent!
                </Typography>

                <Typography
                    variant="h6"
                    sx={{
                        fontSize: { xs: "16px", sm: "18px", md: "20px" },
                        color: "#555555",
                        fontWeight: "normal",
                    }}
                >
                    Singing 🎤, Rapping 🎶, Dancing 💃, Acting 🎭, Magic ✨, Comedy 😂, Art 🎨, Photography 📸, Instrumental 🎸, and more!
                </Typography>
            </Box>

            {/* Main content shifted upwards */}
            <Grid container spacing={6} sx={{ 
                flexGrow: 1, 
                alignItems: 'center',
                justifyContent: 'center',
                mt: { xs: -2, md: -3 }  // Reduced margin top to shift everything up
            }}>
                {/* Logo Column */}
                {!isSmallScreen && (
                    <Grid item xs={12} md={5} sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        mt: { xs: -2, md: -4 }  // Negative margin to shift up
                    }}>
                        <Box sx={{ maxWidth: '200px', width: '100%' }}>
                            <img 
                                src={logo}
                                alt="Login Illustration" 
                                style={{ width: '100%', height: 'auto', borderRadius: '10px' }} 
                            />
                        </Box>
                    </Grid>
                )}
                
                {/* Form Column */}
                <Grid item xs={12} sm={10} md={6} sx={{
                    mt: { xs: -3, md: -4 }  // Negative margin to shift up
                }}>
                    <Formik
                        onSubmit={handleFormSubmit}
                        initialValues={initialValuesLogin}
                        validationSchema={loginSchema}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            resetForm,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    sx={{ 
                                        width: '100%',
                                        minWidth: { sm: '500px' },
                                        maxWidth: '700px',
                                        margin: '0 auto',  // Changed from '85px auto' to shift up
                                        padding: { xs: '20px', sm: '30px' },
                                        border: '1px solid #ddd',
                                        borderRadius: '24px',
                                        backgroundColor: '#fff',
                                    }}
                                >
                                    {/* Mobile logo display */}
                                    {isSmallScreen && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'center', 
                                            mb: 3,
                                            maxWidth: '100px',
                                            mx: 'auto'
                                        }}>
                                            <img 
                                                src={logo}
                                                alt="Login Illustration" 
                                                style={{ width: '100%', height: 'auto', borderRadius: '10px' }} 
                                            />
                                        </Box>
                                    )}

                                    <Box
                                        display="grid"
                                        gap="20px"
                                        gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                                    >                       
                                        <TextField
                                            label={
                                                <>
                                                    Email<span style={{ color: 'red' }}>*</span>
                                                </>
                                            }
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.email}
                                            name="email"
                                            error={Boolean(touched.email) && Boolean(errors.email)}
                                            helperText={touched.email && errors.email}
                                            sx={{
                                                gridColumn: "span 4",
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "50px", 
                                                    '& fieldset': {
                                                        borderColor: '#ccc',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#FF61D2',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#FF61D2',
                                                        borderWidth: '2px',
                                                    },
                                                },
                                                "& .MuiInputLabel-root": {
                                                    color: '#999',
                                                },
                                                "& .MuiInputLabel-root.Mui-focused": {
                                                    color: '#FF61D2',
                                                },
                                            }}
                                        />

                                        <TextField
                                            label={
                                                <>
                                                    Password<span style={{ color: 'red' }}>*</span>
                                                </>
                                            }
                                            type={passwordVisible ? 'text' : 'password'}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.password}
                                            name="password"
                                            error={Boolean(touched.password) && Boolean(errors.password)}
                                            helperText={touched.password && errors.password}
                                            InputProps={{
                                                endAdornment: (
                                                    <span
                                                        style={{
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            marginRight: '10px',
                                                            color: '#999'
                                                        }}
                                                        onClick={() => setPasswordVisible((prev) => !prev)}
                                                    >
                                                        {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                                                    </span>
                                                ),
                                            }}
                                            sx={{
                                                gridColumn: "span 4",
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "50px",
                                                    '& fieldset': {
                                                        borderColor: '#ccc',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#FF61D2',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#FF61D2',
                                                        borderWidth: '2px',
                                                    },
                                                },
                                                "& .MuiInputLabel-root": {
                                                    color: '#999',
                                                },
                                                "& .MuiInputLabel-root.Mui-focused": {
                                                    color: '#FF61D2',
                                                },
                                            }}
                                        />
                                    </Box>
                                    
                                    {/* Buttons */}
                                    <Box>
                                        <Button
                                            fullWidth
                                            type="submit"
                                            sx={{
                                                m: "1.5rem 0",
                                                p: "0.75rem",
                                                background: "linear-gradient(135deg, #FF61D2 0%, #FE9090 100%)",
                                                color: "#fff",
                                                borderRadius: "50px",
                                                transition: "all 0.3s ease",
                                                "&:hover": {
                                                    background: "linear-gradient(135deg, #FE9090 0%, #FF61D2 100%)",
                                                    transform: "translateY(-2px)",
                                                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                                },
                                            }}
                                        >
                                            LOGIN
                                        </Button>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                            <Typography
                                                onClick={() => {
                                                    navigate("/signup");
                                                    resetForm();
                                                }}
                                                sx={{
                                                    color: "#555555",
                                                    fontSize: "14px",
                                                    "&:hover": {
                                                        cursor: "pointer",
                                                        textDecoration: "underline",
                                                    },
                                                }}
                                            >
                                                Sign Up
                                            </Typography>

                                            <Typography
                                                onClick={() => navigate("/forgot-password")}
                                                sx={{
                                                    color: "#555555",
                                                    fontSize: "14px",
                                                    "&:hover": {
                                                        cursor: "pointer",
                                                        textDecoration: "underline",
                                                    },
                                                }}
                                            >
                                                Forgot Password?
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Grid>
            </Grid>

            {/* Warning section - Now positioned fixed at the bottom */}
            <Box 
                sx={{
                    position: 'fixed',
                    bottom: { xs: '10px', sm: '15px', md: '20px' },
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: { xs: '90%', sm: '80%', md: '70%' },
                    textAlign: 'center',
                    padding: '10px',
                    backgroundColor: 'rgba(255, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 0, 0, 0.2)',
                    borderRadius: '8px',
                    zIndex: 1000, // Ensure it appears above other content
                }}
            >
                <Typography 
                    variant="body2" 
                    sx={{ 
                        color: '#c41c1c',
                        fontWeight: 'medium',
                        fontSize: { xs: '12px', sm: '14px' },
                    }}
                >
                    ⚠️ Talent Content Only!
                    Only upload content under above supported or creative talent categories.
                    Violations may lead to removal or account action.
                </Typography>
            </Box>
        </Container>
    );
};

export default Login;